import { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSaveVignette } from '../../hooks/useApi';
import { errorMessage } from '../../services/api';
import { toast } from '../../store';
import type { Vignette, VignetteCountry, VignetteInput, VignettePeriod } from '../../types';
import { normalizeForm, toDateInput, toOptions, VIGNETTE_COUNTRIES, VIGNETTE_PERIOD_DAYS, VIGNETTE_PERIOD_LABELS } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { CarSelect } from './EntitySelects';
import { t } from '../../i18n';

type FormValues = Record<keyof VignetteInput, string>;

/** Pre-filled values for a new vignette, e.g. a renewal from the overview. */
export interface VignetteDraft {
  car_id?: number;
  country?: VignetteCountry;
  valid_from?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  vignette?: Vignette | null;
  draft?: VignetteDraft;
}

function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return toDateInput(new Date(y, m - 1, d + days));
}

export function VignetteFormModal({ isOpen, onClose, vignette, draft }: Props) {
  const save = useSaveVignette();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>();

  useLayoutEffect(() => {
    if (!isOpen) return;
    reset({
      car_id: String(vignette?.car_id ?? draft?.car_id ?? ''),
      country: vignette?.country ?? draft?.country ?? 'SK',
      period: vignette?.period ?? '',
      valid_from: vignette?.valid_from ?? draft?.valid_from ?? toDateInput(),
      valid_to: vignette?.valid_to ?? '',
      price: vignette?.price?.toString() ?? '',
      reference: vignette?.reference ?? '',
      notes: vignette?.notes ?? '',
    });
  }, [isOpen, vignette, draft, reset]);

  // Choosing a type suggests the end date; it stays editable (rules differ per country)
  const suggestEnd = () => {
    const { period, valid_from } = getValues();
    if (period && valid_from) setValue('valid_to', addDays(valid_from, VIGNETTE_PERIOD_DAYS[period as VignettePeriod] - 1), { shouldValidate: true });
  };

  const onSubmit = async (values: FormValues) => {
    const data = normalizeForm(values, ['car_id', 'price']) as unknown as VignetteInput;
    if (vignette) data.car_id = vignette.car_id;
    try {
      await save.mutateAsync({ id: vignette?.id, data });
      toast.success(vignette ? t('Známka bola uložená') : t('Známka bola pridaná'));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Známku sa nepodarilo uložiť')));
    }
  };

  const countryOptions = (Object.keys(VIGNETTE_COUNTRIES) as VignetteCountry[]).map((code) => ({ value: code, label: `${code} – ${VIGNETTE_COUNTRIES[code].name}` }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={save.isPending}
      size="lg"
      title={vignette ? t('Upraviť diaľničnú známku') : t('Nová diaľničná známka')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="vignette-form" loading={save.isPending}>
            {vignette ? t('Uložiť') : t('Pridať')}
          </Button>
        </>
      }
    >
      <form id="vignette-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <CarSelect
          label={t('Vozidlo')}
          required
          disabled={!!vignette}
          error={errors.car_id?.message}
          {...register('car_id', { required: vignette ? false : t('Vyberte vozidlo') })}
        />
        <Select label={t('Krajina')} required options={countryOptions} {...register('country')} />
        <Select
          label={t('Typ známky')}
          placeholder={t('Nevyplnené')}
          options={toOptions(VIGNETTE_PERIOD_LABELS)}
          {...register('period', { onChange: suggestEnd })}
        />
        <Input label={t('Cena (€)')} type="number" step="0.01" min={0} {...register('price', { validate: (v) => !v || Number(v) >= 0 || t('Nesmie byť záporné') })} />
        <Input
          label={t('Platná od')}
          type="date"
          required
          error={errors.valid_from?.message}
          {...register('valid_from', { required: t('Zadajte dátum'), onChange: suggestEnd })}
        />
        <Input
          label={t('Platná do')}
          type="date"
          required
          hint={t('Doplní sa podľa typu – skontrolujte podľa dokladu o kúpe')}
          error={errors.valid_to?.message}
          {...register('valid_to', {
            required: t('Zadajte dátum'),
            validate: (value, all) => !all.valid_from || value >= all.valid_from || t('Nesmie byť skôr ako „platná od“'),
          })}
        />
        <Input label={t('Číslo dokladu / referencia')} wrapperClassName="sm:col-span-2" {...register('reference')} />
        <Textarea label={t('Poznámka')} wrapperClassName="sm:col-span-2" rows={2} {...register('notes')} />
      </form>
    </Modal>
  );
}
